/**
 * SEMANTIC EXTENSIONS SERVICE
 * 
 * Servicio para gestionar extensiones dinámicas de la Semantic Layer.
 * 
 * FUNCIONES:
 * 1. Cargar extensiones de Firestore
 * 2. Combinar con ontología base
 * 3. Agregar nuevos sinónimos/conceptos
 * 4. Trackear términos no encontrados
 * 5. Gestionar términos de terapeutas
 * 
 * USO:
 * - Se inicializa al arrancar la app
 * - Cachea extensiones en memoria
 * - Refresca cada 5 minutos o bajo demanda
 */

import { db, FieldValue } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
  CustomSynonym,
  CustomConcept,
  TherapistTerm,
  UnmatchedTerm,
  LearnedAssociation,
  SemanticLayerConfig,
  DEFAULT_SEMANTIC_CONFIG,
  AddSynonymDTO,
  AddConceptDTO,
  AddTherapistTermDTO,
  ReviewUnmatchedTermDTO
} from './extensions.model';
import { CONCEPT_ONTOLOGY, ConceptId } from './ontology';

// Tipo para nodos de concepto usados internamente
interface ConceptNode {
  id: string;
  label: string;
  parent?: string;
  synonyms: string[];
}

const logger = Logger.getLogger('SemanticExtensions');

// Colecciones
const COLLECTIONS = {
  config: 'semantic_config',
  synonyms: 'semantic_custom_synonyms',
  concepts: 'semantic_custom_concepts',
  therapistTerms: 'semantic_therapist_terms',
  unmatchedTerms: 'semantic_unmatched_terms',
  learnedAssociations: 'semantic_learned_associations',
} as const;

interface CachedExtensions {
  synonyms: Map<string, ConceptId[]>;      // term -> conceptIds
  concepts: Map<string, ConceptNode>;       // conceptId -> concept
  therapistTerms: Map<string, Map<string, ConceptId[]>>;  // therapistId -> term -> conceptIds
  config: SemanticLayerConfig;
  lastRefreshed: number;
}

class SemanticExtensionsService {
  private cache: CachedExtensions | null = null;
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos
  private refreshPromise: Promise<void> | null = null;

  // ============================================
  // INICIALIZACIÓN Y CACHE
  // ============================================

  /**
   * Inicializa el servicio, cargando extensiones de Firestore
   */
  async initialize(): Promise<void> {
    await this.refreshCache();
    logger.info('SemanticExtensions inicializado', {
      synonyms: this.cache?.synonyms.size ?? 0,
      concepts: this.cache?.concepts.size ?? 0,
    });
  }

  /**
   * Refresca el cache de extensiones
   */
  async refreshCache(): Promise<void> {
    // Evitar múltiples refreshes simultáneos
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<void> {
    try {
      // 1. Cargar config
      const config = await this.loadConfig();

      // 2. Cargar sinónimos
      const synonyms = await this.loadSynonyms();

      // 3. Cargar conceptos
      const concepts = await this.loadConcepts();

      // 4. Cargar términos de terapeutas
      const therapistTerms = await this.loadTherapistTerms();

      this.cache = {
        synonyms,
        concepts,
        therapistTerms,
        config,
        lastRefreshed: Date.now(),
      };

      logger.debug('Cache refrescado', {
        synonyms: synonyms.size,
        concepts: concepts.size,
        therapistCount: therapistTerms.size,
      });
    } catch (error) {
      logger.error('Error refrescando cache', error instanceof Error ? error : new Error('Unknown error'));
      // Si falla, usar cache anterior o defaults
      if (!this.cache) {
        this.cache = {
          synonyms: new Map(),
          concepts: new Map(),
          therapistTerms: new Map(),
          config: DEFAULT_SEMANTIC_CONFIG,
          lastRefreshed: Date.now(),
        };
      }
    }
  }

  private async loadConfig(): Promise<SemanticLayerConfig> {
    try {
      const configRef = db.collection(COLLECTIONS.config).doc('config');
      const configSnap = await configRef.get();

      if (configSnap.exists) {
        return configSnap.data() as SemanticLayerConfig;
      }

      // Crear config por defecto si no existe
      await configRef.set(DEFAULT_SEMANTIC_CONFIG);
      return DEFAULT_SEMANTIC_CONFIG;
    } catch (error) {
      logger.warn('Error cargando config, usando defaults', error instanceof Error ? error : new Error('Unknown error'));
      return DEFAULT_SEMANTIC_CONFIG;
    }
  }

  private async loadSynonyms(): Promise<Map<string, ConceptId[]>> {
    const synonyms = new Map<string, ConceptId[]>();

    try {
      const snapshot = await db.collection(COLLECTIONS.synonyms)
        .where('isActive', '==', true)
        .get();

      snapshot.forEach((doc: any) => {
        const data = doc.data() as CustomSynonym;
        const existing = synonyms.get(data.termNormalized) || [];
        existing.push(data.mapsToConceptId as ConceptId);
        synonyms.set(data.termNormalized, existing);
      });
    } catch (error) {
      logger.warn('Error cargando sinónimos custom', error instanceof Error ? error : new Error('Unknown error'));
    }

    return synonyms;
  }

  private async loadConcepts(): Promise<Map<string, ConceptNode>> {
    const concepts = new Map<string, ConceptNode>();

    try {
      const snapshot = await db.collection(COLLECTIONS.concepts)
        .where('isActive', '==', true)
        .get();

      snapshot.forEach((docSnap: any) => {
        const data = docSnap.data() as CustomConcept;
        const conceptNode: ConceptNode = {
          id: data.conceptId,
          label: data.labelEs,
          parent: data.parentId,
          synonyms: data.synonyms,
        };
        concepts.set(data.conceptId, conceptNode);
      });
    } catch (error) {
      logger.warn('Error cargando conceptos custom', error instanceof Error ? error : new Error('Unknown error'));
    }

    return concepts;
  }

  private async loadTherapistTerms(): Promise<Map<string, Map<string, ConceptId[]>>> {
    const terms = new Map<string, Map<string, ConceptId[]>>();

    try {
      const snapshot = await db.collection(COLLECTIONS.therapistTerms)
        .where('isActive', '==', true)
        .get();

      snapshot.forEach((docSnap: any) => {
        const data = docSnap.data() as TherapistTerm;

        if (!terms.has(data.therapistId)) {
          terms.set(data.therapistId, new Map());
        }

        const therapistMap = terms.get(data.therapistId)!;
        const existing = therapistMap.get(data.termNormalized) || [];
        existing.push(data.mapsToConceptId as ConceptId);
        therapistMap.set(data.termNormalized, existing);
      });
    } catch (error) {
      logger.warn('Error cargando términos de terapeutas', error instanceof Error ? error : new Error('Unknown error'));
    }

    return terms;
  }

  // ============================================
  // LOOKUP COMBINADO (Base + Extensiones)
  // ============================================

  /**
   * Busca un término en la ontología combinada
   * Prioridad: Extensiones > Base > Terapeuta
   */
  lookupTerm(term: string, therapistId?: string): ConceptId[] {
    const normalized = this.normalize(term);
    const results = new Set<ConceptId>();

    // 1. Buscar en extensiones globales
    const extensionMatches = this.cache?.synonyms.get(normalized);
    if (extensionMatches) {
      extensionMatches.forEach(id => results.add(id));
    }

    // 2. Buscar en ontología base (sinónimos)
    for (const concept of Object.values(CONCEPT_ONTOLOGY)) {
      if (concept.synonyms) {
        for (const synonym of concept.synonyms) {
          if (this.normalize(synonym) === normalized) {
            results.add(concept.id as ConceptId);
          }
        }
      }
      // También buscar por label
      if (this.normalize(concept.label) === normalized) {
        results.add(concept.id as ConceptId);
      }
    }

    // 3. Buscar en términos del terapeuta específico
    if (therapistId && this.cache?.therapistTerms.has(therapistId)) {
      const therapistMap = this.cache.therapistTerms.get(therapistId)!;
      const therapistMatches = therapistMap.get(normalized);
      if (therapistMatches) {
        therapistMatches.forEach(id => results.add(id));
      }
    }

    return Array.from(results);
  }

  /**
   * Obtiene un concepto por ID (base o extensión)
   */
  getConcept(conceptId: string): ConceptNode | undefined {
    // Primero buscar en base
    const baseConcept = CONCEPT_ONTOLOGY[conceptId];
    if (baseConcept) return baseConcept;

    // Luego en extensiones
    return this.cache?.concepts.get(conceptId);
  }

  /**
   * Obtiene todos los sinónimos para un concepto
   */
  getAllSynonyms(conceptId: ConceptId): string[] {
    const synonyms: string[] = [];

    // 1. Sinónimos de la base
    const baseConcept = CONCEPT_ONTOLOGY[conceptId];
    if (baseConcept?.synonyms) {
      synonyms.push(...baseConcept.synonyms);
    }

    // 2. Sinónimos de extensiones
    if (this.cache) {
      for (const [term, ids] of Array.from(this.cache.synonyms.entries())) {
        if (ids.includes(conceptId)) {
          synonyms.push(term);
        }
      }
    }

    return [...new Set(synonyms)];
  }

  // ============================================
  // GESTIÓN DE EXTENSIONES (Admin)
  // ============================================

  /**
   * Agrega un nuevo sinónimo (Admin only)
   */
  async addSynonym(dto: AddSynonymDTO, adminId: string): Promise<string> {
    const id = `syn_${Date.now()}`;
    const normalized = this.normalize(dto.term);

    const synonym: CustomSynonym = {
      id,
      term: dto.term,
      termNormalized: normalized,
      mapsToConceptId: dto.mapsToConceptId,
      addedBy: adminId,
      addedAt: new Date().toISOString(),
      reason: dto.reason,
      isActive: true,
      usageCount: 0,
    };

    await db.collection(COLLECTIONS.synonyms).doc(id).set(synonym);

    // Actualizar cache inmediatamente
    if (this.cache) {
      const existing = this.cache.synonyms.get(normalized) || [];
      existing.push(dto.mapsToConceptId);
      this.cache.synonyms.set(normalized, existing);
    }

    logger.info('Sinónimo agregado', { id, term: dto.term, mapsTo: dto.mapsToConceptId });
    return id;
  }

  /**
   * Agrega un nuevo concepto (Admin only)
   */
  async addConcept(dto: AddConceptDTO, adminId: string): Promise<string> {
    const id = `concept_${dto.conceptId}`;

    // Validar que el parent existe
    const parentExists = CONCEPT_ONTOLOGY[dto.parentId] || this.cache?.concepts.has(dto.parentId);
    if (!parentExists) {
      throw new Error(`Parent concept ${dto.parentId} no existe`);
    }

    const concept: CustomConcept = {
      id,
      conceptId: dto.conceptId,
      label: dto.label,
      labelEs: dto.labelEs,
      parentId: dto.parentId,
      level: 3, // Siempre nivel 3 para extensiones
      synonyms: dto.synonyms,
      addedBy: adminId,
      addedAt: new Date().toISOString(),
      reason: dto.reason,
      isActive: true,
      usageCount: 0,
    };

    await db.collection(COLLECTIONS.concepts).doc(id).set(concept);

    // Actualizar cache
    if (this.cache) {
      this.cache.concepts.set(dto.conceptId, {
        id: dto.conceptId,
        label: dto.labelEs,
        parent: dto.parentId,
        synonyms: dto.synonyms,
      });
    }

    logger.info('Concepto agregado', { id: dto.conceptId, parent: dto.parentId });
    return id;
  }

  // ============================================
  // TÉRMINOS DE TERAPEUTAS
  // ============================================

  /**
   * Agrega un término personalizado de un terapeuta
   */
  async addTherapistTerm(dto: AddTherapistTermDTO): Promise<string> {
    const config = this.cache?.config || DEFAULT_SEMANTIC_CONFIG;

    // Verificar límite
    const therapistTerms = this.cache?.therapistTerms.get(dto.therapistId);
    if (therapistTerms && therapistTerms.size >= config.therapistExtensions.maxTermsPerTherapist) {
      throw new Error(`Límite de términos alcanzado (${config.therapistExtensions.maxTermsPerTherapist})`);
    }

    const id = `term_${dto.therapistId}_${Date.now()}`;
    const normalized = this.normalize(dto.term);

    const term: TherapistTerm = {
      id,
      therapistId: dto.therapistId,
      term: dto.term,
      termNormalized: normalized,
      mapsToConceptId: dto.mapsToConceptId,
      mapsToPracticeId: dto.mapsToPracticeId,
      notes: dto.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      usageCount: 0,
    };

    await db.collection(COLLECTIONS.therapistTerms).doc(id).set(term);

    // Actualizar cache
    if (this.cache) {
      if (!this.cache.therapistTerms.has(dto.therapistId)) {
        this.cache.therapistTerms.set(dto.therapistId, new Map());
      }
      const tMap = this.cache.therapistTerms.get(dto.therapistId)!;
      const existing = tMap.get(normalized) || [];
      existing.push(dto.mapsToConceptId);
      tMap.set(normalized, existing);
    }

    logger.info('Término de terapeuta agregado', {
      therapistId: dto.therapistId,
      term: dto.term
    });
    return id;
  }

  /**
   * Obtiene todos los términos de un terapeuta
   */
  async getTherapistTerms(therapistId: string): Promise<TherapistTerm[]> {
    const snapshot = await db.collection(COLLECTIONS.therapistTerms)
      .where('therapistId', '==', therapistId)
      .where('isActive', '==', true)
      .get();
    return snapshot.docs.map((d: any) => d.data() as TherapistTerm);
  }

  // ============================================
  // TRACKING DE TÉRMINOS NO ENCONTRADOS
  // ============================================

  /**
   * Registra un término que no se encontró
   * Útil para detectar gaps en la ontología
   */
  async trackUnmatchedTerm(
    term: string,
    context: 'user_library' | 'therapist_library' | 'ai_assistant' | 'practice_search',
    userId?: string,
    therapistId?: string
  ): Promise<void> {
    const config = this.cache?.config || DEFAULT_SEMANTIC_CONFIG;
    if (!config.learning.enabled) return;

    const normalized = this.normalize(term);

    // Buscar si ya existe
    const snapshot = await db.collection(COLLECTIONS.unmatchedTerms)
      .where('termNormalized', '==', normalized)
      .get();

    if (snapshot.empty) {
      // Crear nuevo
      const id = `unmatched_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const record: UnmatchedTerm = {
        id,
        term,
        termNormalized: normalized,
        searchContext: context,
        userId,
        therapistId,
        firstSeenAt: new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
        occurrences: 1,
        reviewStatus: 'pending',
      };
      await db.collection(COLLECTIONS.unmatchedTerms).doc(id).set(record);
    } else {
      // Actualizar existente
      const existing = snapshot.docs[0];
      await existing.ref.update({
        lastSeenAt: new Date().toISOString(),
        occurrences: FieldValue.increment(1),
      });
    }
  }

  /**
   * Obtiene términos no encontrados pendientes de revisión
   */
  async getPendingUnmatchedTerms(limitCount: number = 50): Promise<UnmatchedTerm[]> {
    const config = this.cache?.config || DEFAULT_SEMANTIC_CONFIG;

    const snapshot = await db.collection(COLLECTIONS.unmatchedTerms)
      .where('reviewStatus', '==', 'pending')
      .where('occurrences', '>=', config.learning.minOccurrencesToSuggest)
      .orderBy('occurrences', 'desc')
      .limit(limitCount)
      .get();

    return snapshot.docs.map((d: any) => d.data() as UnmatchedTerm);
  }

  /**
   * Revisa un término no encontrado (Admin)
   */
  async reviewUnmatchedTerm(dto: ReviewUnmatchedTermDTO, adminId: string): Promise<void> {
    const termRef = db.collection(COLLECTIONS.unmatchedTerms).doc(dto.termId);
    const termSnap = await termRef.get();

    if (!termSnap.exists) {
      throw new Error('Término no encontrado');
    }

    const term = termSnap.data() as UnmatchedTerm;

    if (dto.action === 'map' && dto.mapsToConceptId) {
      // Mapear a concepto
      await termRef.update({
        reviewStatus: 'mapped',
        reviewedBy: adminId,
        reviewedAt: new Date().toISOString(),
        mappedToConceptId: dto.mapsToConceptId,
      });

      // Crear sinónimo automáticamente
      await this.addSynonym({
        term: term.term,
        mapsToConceptId: dto.mapsToConceptId,
        reason: `Auto-generado desde término no encontrado (${term.occurrences} ocurrencias)`,
      }, adminId);

    } else if (dto.action === 'ignore') {
      await termRef.update({
        reviewStatus: 'ignored',
        reviewedBy: adminId,
        reviewedAt: new Date().toISOString(),
        ignoreReason: dto.ignoreReason,
      });
    }

    logger.info('Término revisado', {
      termId: dto.termId,
      action: dto.action,
      mapsTo: dto.mapsToConceptId
    });
  }

  // ============================================
  // ASOCIACIONES APRENDIDAS
  // ============================================

  /**
   * Registra una asociación entre input y resultado útil
   */
  async recordAssociation(
    input: string,
    conceptId?: ConceptId,
    practiceId?: string,
    wasEffective: boolean = false
  ): Promise<void> {
    const config = this.cache?.config || DEFAULT_SEMANTIC_CONFIG;
    if (!config.learning.enabled) return;

    const normalized = this.normalize(input);

    // Buscar si ya existe
    let query = db.collection(COLLECTIONS.learnedAssociations)
      .where('inputNormalized', '==', normalized);

    // Firebase no permite null en where, así que hacemos la comparación después
    const snapshot = await query.get();

    // Filtrar en memoria por conceptId y practiceId
    const matchingDoc = snapshot.docs.find((doc: any) => {
      const data = doc.data();
      return data.associatedConceptId === (conceptId || null) &&
        data.associatedPracticeId === (practiceId || null);
    });

    if (!matchingDoc) {
      // Crear nueva
      const id = `assoc_${Date.now()}`;
      const association: LearnedAssociation = {
        id,
        inputPattern: input,
        inputNormalized: normalized,
        associatedConceptId: conceptId,
        associatedPracticeId: practiceId,
        timesUsed: 1,
        timesEffective: wasEffective ? 1 : 0,
        effectivenessScore: wasEffective ? 1 : 0,
        firstSeenAt: new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
        isActive: true,
        confidenceLevel: 'low',
      };
      await db.collection(COLLECTIONS.learnedAssociations).doc(id).set(association);
    } else {
      // Actualizar existente
      const data = matchingDoc.data() as LearnedAssociation;
      const newTimesUsed = data.timesUsed + 1;
      const newTimesEffective = data.timesEffective + (wasEffective ? 1 : 0);
      const newScore = newTimesEffective / newTimesUsed;

      await matchingDoc.ref.update({
        timesUsed: newTimesUsed,
        timesEffective: newTimesEffective,
        effectivenessScore: newScore,
        lastSeenAt: new Date().toISOString(),
        confidenceLevel: this.calculateConfidenceLevel(newTimesUsed, newScore),
      });
    }
  }

  private calculateConfidenceLevel(
    timesUsed: number,
    score: number
  ): 'low' | 'medium' | 'high' {
    if (timesUsed < 5) return 'low';
    if (timesUsed < 20 || score < 0.6) return 'medium';
    return 'high';
  }

  // ============================================
  // ESTADÍSTICAS
  // ============================================

  /**
   * Obtiene estadísticas de la Semantic Layer
   */
  async getStats(): Promise<{
    baseConceptsCount: number;
    customSynonymsCount: number;
    customConceptsCount: number;
    therapistsWithTermsCount: number;
    pendingTermsCount: number;
    learnedAssociationsCount: number;
  }> {
    const synonymsSnap = await db.collection(COLLECTIONS.synonyms).get();
    const conceptsSnap = await db.collection(COLLECTIONS.concepts).get();

    const pendingSnap = await db.collection(COLLECTIONS.unmatchedTerms)
      .where('reviewStatus', '==', 'pending')
      .get();

    const learnedSnap = await db.collection(COLLECTIONS.learnedAssociations).get();

    return {
      baseConceptsCount: Object.keys(CONCEPT_ONTOLOGY).length,
      customSynonymsCount: synonymsSnap.size,
      customConceptsCount: conceptsSnap.size,
      therapistsWithTermsCount: this.cache?.therapistTerms.size || 0,
      pendingTermsCount: pendingSnap.size,
      learnedAssociationsCount: learnedSnap.size,
    };
  }

  // ============================================
  // UTILIDADES
  // ============================================

  private normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
      .trim();
  }

  /**
   * Verifica si el cache necesita refresh
   */
  needsRefresh(): boolean {
    if (!this.cache) return true;
    return Date.now() - this.cache.lastRefreshed > this.CACHE_TTL_MS;
  }

  /**
   * Obtiene la configuración actual
   */
  getConfig(): SemanticLayerConfig {
    return this.cache?.config || DEFAULT_SEMANTIC_CONFIG;
  }
}

// Singleton
export const semanticExtensionsService = new SemanticExtensionsService();

